import { useCallback, useEffect, useState } from "react";

import { useErrorHandling } from "./useErrorHandling";

/**
 * Convert async calls to state.
 * This will also ensure the order of the requests.
 * Make sure the getPromise only change when new call is needed! => useCallback ()
 * Did not add a deps array, because linter does not check deps with this custom hook, so caller must wrap the function in a useCallback
 */

export function useAsync<TResult>(
	getPromise: () => Promise<TResult>,
	{
		logErrorToServer = true,
		logErrorToConsole = true,
		defaultValue,
		loadingValue = defaultValue,
		errorValue = defaultValue,
	}: {
		/** Be default, rejections will be logged to the console  */
		logErrorToConsole?: boolean;

		/** Be default, rejections will be logged to the server  */
		logErrorToServer?: boolean;

		/** The default value to use when not yet loaded, loading or errored state */
		defaultValue: TResult;

		/** Use this value (instead of the defaultValue) when loading */
		loadingValue?: TResult;

		/** Use this value (instead of the defaultValue) when errored */
		errorValue?: TResult;
	},
): eXpress.AsyncValue<TResult, any, { prevValue: TResult }> {
	const { createErrorHandler } = useErrorHandling();

	const [asyncValue, setAsyncValue] = useState<
		eXpress.AsyncValue<TResult, any, { prevValue: TResult }>
	>(eXpress.createNotLoadedAsyncValue(defaultValue));

	/** The function to start a new request */
	const startAsync = useCallback(() => {
		// Set Loading State, add also previous value, so a UI can choose to show old data
		setAsyncValue((prevAsyncValue) =>
			eXpress.createLoadingAsyncValue(loadingValue, {
				prevValue: prevAsyncValue.value,
			}),
		);

		// Ensure order of requests (Remark will not work is getPromise changes -> useCallback)
		eXpress
			.detectLastAsync(getPromise)()
			.then((lastRequestResult) => {
				if (!lastRequestResult.getIsLastRequested()) return; // ignore old results
				const loadedState = eXpress.createLoadedAsyncValue(lastRequestResult.value);
				setAsyncValue(loadedState);
				// No need to return
			})
			.catch((error: unknown) => {
				if (!eXpress.isLastRequested(error as eXpress.IDetectLastResult<unknown>)) return; // ignore old errors
				createErrorHandler({
					logErrorToConsole,
					logErrorToServer,
				})(error);

				const errorState = eXpress.createErroredAsyncValue(errorValue, error);
				setAsyncValue(errorState);
				// Don't rethrow -> so no unhandled execeptions
			});
		// React Hook useCallback has a missing dependency: 'TRequest': seems like a bug
	}, [
		createErrorHandler,
		errorValue,
		getPromise,
		loadingValue,
		logErrorToConsole,
		logErrorToServer,
	]);

	// If the start function changes, it will rerun and update state
	useEffect(() => {
		startAsync();
	}, [startAsync]);

	return asyncValue;
}
